"use client";

import { useState } from "react";
import Link from "next/link";
import { startRegistration } from "@simplewebauthn/browser";
import type { PublicKeyCredentialCreationOptionsJSON } from "@simplewebauthn/browser";
import { useSession } from "next-auth/react";
import { UserProfile } from "@/utils/types";
import { removePasskey } from "@/services/passkey.service";
import { getContractExplorerUrl } from "@/lib/hoops-contracts";

interface PasskeysSectionProps {
  profileData: UserProfile | null;
  onProfileUpdated: () => void;
}

const ghostButtonStyle: React.CSSProperties = {
  color: "#888",
  background: "#111",
  border: "1px solid #1e1e1e",
  borderRadius: "8px",
  padding: "9px 16px",
  display: "flex",
  alignItems: "center",
  gap: "7px",
  cursor: "pointer",
  fontSize: "11px",
  fontWeight: 500,
  letterSpacing: "0.06em",
  transition: "border-color 0.15s, color 0.15s",
};

function truncate(value: string, head = 6, tail = 6): string {
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}...${value.slice(-tail)}`;
}

function formatDeviceType(deviceType: string): string {
  if (deviceType === "singleDevice") return "Single-device";
  if (deviceType === "multiDevice") return "Multi-device (synced)";
  return deviceType;
}

export function PasskeysSection({ profileData, onProfileUpdated }: PasskeysSectionProps) {
  const { data: session } = useSession();
  const [linkingPasskey, setLinkingPasskey] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const [removingPasskeyId, setRemovingPasskeyId] = useState<string | null>(null);

  async function handleLinkPasskey() {
    try {
      setPasskeyError(null);
      setLinkingPasskey(true);

      const optionsRes = await fetch("/api/auth/v2/passkey/add/options", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!optionsRes.ok) {
        const err = (await optionsRes.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? `Failed to get passkey options (${String(optionsRes.status)})`);
      }
      const { challengeId, options } = (await optionsRes.json()) as {
        challengeId: string;
        options: PublicKeyCredentialCreationOptionsJSON;
        userHandle?: string;
      };

      const attestation = await startRegistration({ optionsJSON: options });

      const verifyRes = await fetch("/api/auth/v2/passkey/add/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeId, attestation }),
      });
      if (!verifyRes.ok) {
        const err = (await verifyRes.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? `Failed to verify passkey (${String(verifyRes.status)})`);
      }

      onProfileUpdated();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to link passkey";
      setPasskeyError(message);
    } finally {
      setLinkingPasskey(false);
    }
  }

  async function handleRemovePasskey(credId: string) {
    try {
      setRemovingPasskeyId(credId);
      await removePasskey(credId);
      onProfileUpdated();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to remove passkey";
      setPasskeyError(message);
    } finally {
      setRemovingPasskeyId(null);
    }
  }

  const hasPasskey = session?.user.hasPasskey === true;
  const smartAccountAddress = session?.user.smart_account_address;
  const sessionCredentialId = session?.user.credentialId;
  const list = profileData?.passkeys ?? [];

  // Resolve a display credential ID: prefer full list, fall back to session token
  const primaryPasskey = list.length > 0 ? list[0] : null;
  const displayCredId = primaryPasskey?.credId ?? sessionCredentialId ?? null;

  return (
    <div
      style={{
        background: "#0e0e0e",
        border: "1px solid #1c1c1c",
        borderRadius: "14px",
        overflow: "hidden",
        marginBottom: "10px",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "18px 20px",
          borderBottom: "1px solid #1c1c1c",
        }}
      >
        <div>
          <p style={{ fontSize: "12px", fontWeight: 600, letterSpacing: "0.08em", color: "#c8c3ba", margin: 0 }}>
            PASSKEYS
          </p>
          <p style={{ fontSize: "10px", color: "#4a4a4a", letterSpacing: "0.02em", marginTop: "2px" }}>
            Hardware-backed biometric authenticators
          </p>
        </div>
        {hasPasskey && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "4px 10px",
              background: "#0a1a0f",
              border: "1px solid #1a3a22",
              borderRadius: "20px",
            }}
          >
            <span
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: "#3a8f5a",
                boxShadow: "0 0 6px #3a8f5a88",
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: "10px", fontWeight: 500, color: "#4ab870", letterSpacing: "0.05em" }}>
              Active
            </span>
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: "18px 20px" }}>

        {hasPasskey ? (
          <>
            {/* Status card */}
            <div
              style={{
                padding: "14px 16px",
                background: "#0a0a0a",
                border: "1px solid #1c1c1c",
                borderRadius: "10px",
                marginBottom: "12px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
                <span style={{ fontSize: "15px", lineHeight: 1 }}>&#128274;</span>
                <span style={{ fontSize: "11px", fontWeight: 600, color: "#c8c3ba", letterSpacing: "0.04em" }}>
                  Passkey Active
                </span>
              </div>

              {displayCredId && (
                <div style={{ marginBottom: "6px" }}>
                  <span style={{ fontSize: "10px", color: "#4a4a4a", letterSpacing: "0.04em" }}>
                    CREDENTIAL ID
                  </span>
                  <p style={{ fontSize: "11px", color: "#666", fontFamily: "monospace", margin: "2px 0 0" }}>
                    {truncate(displayCredId, 10, 8)}
                  </p>
                </div>
              )}

              {primaryPasskey?.deviceType && (
                <div style={{ marginBottom: "6px" }}>
                  <span style={{ fontSize: "10px", color: "#4a4a4a", letterSpacing: "0.04em" }}>
                    DEVICE TYPE
                  </span>
                  <p style={{ fontSize: "11px", color: "#666", margin: "2px 0 0" }}>
                    {formatDeviceType(primaryPasskey.deviceType)}
                    {primaryPasskey.backedUp && (
                      <span style={{ color: "#4ab870", marginLeft: "6px" }}>&bull; Backed up</span>
                    )}
                  </p>
                </div>
              )}

              {primaryPasskey?.createdAt && (
                <div>
                  <span style={{ fontSize: "10px", color: "#4a4a4a", letterSpacing: "0.04em" }}>
                    ADDED
                  </span>
                  <p style={{ fontSize: "11px", color: "#666", margin: "2px 0 0" }}>
                    {new Date(primaryPasskey.createdAt).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </p>
                </div>
              )}
            </div>

            {/* Smart account link */}
            {smartAccountAddress && (
              <div
                style={{
                  padding: "12px 16px",
                  background: "#0a0a0a",
                  border: "1px solid #1c1c1c",
                  borderRadius: "10px",
                  marginBottom: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                }}
              >
                <div>
                  <span style={{ fontSize: "10px", color: "#4a4a4a", letterSpacing: "0.04em", display: "block" }}>
                    LINKED SMART ACCOUNT
                  </span>
                  <p style={{ fontSize: "11px", color: "#666", fontFamily: "monospace", margin: "2px 0 0" }}>
                    {truncate(smartAccountAddress, 8, 8)}
                  </p>
                </div>
                <a
                  href={getContractExplorerUrl(smartAccountAddress)}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontSize: "10px",
                    fontWeight: 500,
                    letterSpacing: "0.05em",
                    color: "#555",
                    background: "transparent",
                    border: "1px solid #222",
                    borderRadius: "7px",
                    padding: "5px 12px",
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                    transition: "color 0.15s, border-color 0.15s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#c8c3ba";
                    e.currentTarget.style.borderColor = "#333";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#555";
                    e.currentTarget.style.borderColor = "#222";
                  }}
                >
                  Explorer &rarr;
                </a>
              </div>
            )}

            {passkeyError && (
              <p style={{ fontSize: "11px", color: "#a85555", marginBottom: "12px" }}>
                {passkeyError}
              </p>
            )}

            {/* Per-passkey list (multiple passkeys) */}
            {list.length > 1 && (
              <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "12px" }}>
                {list.map((pk) => {
                  const created = pk.createdAt
                    ? new Date(pk.createdAt).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })
                    : "";
                  return (
                    <div
                      key={pk.credId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                        padding: "10px 14px",
                        background: "#0a0a0a",
                        border: "1px solid #1c1c1c",
                        borderRadius: "8px",
                      }}
                    >
                      <div>
                        <p style={{ fontSize: "11px", fontWeight: 500, color: "#c8c3ba", margin: 0 }}>
                          {pk.nickname ?? `Passkey \u2022 ${pk.credId.slice(-8)}`}
                        </p>
                        <p style={{ fontSize: "10px", color: "#333", marginTop: "2px" }}>
                          {created && `Added ${created}`}
                          {pk.deviceType ? ` \u2022 ${formatDeviceType(pk.deviceType)}` : ""}
                        </p>
                      </div>
                      <button
                        onClick={() => { void handleRemovePasskey(pk.credId); }}
                        disabled={removingPasskeyId === pk.credId}
                        style={{
                          fontSize: "10px",
                          fontWeight: 500,
                          letterSpacing: "0.06em",
                          color: "#a85555",
                          background: "transparent",
                          border: "1px solid #3a1818",
                          borderRadius: "7px",
                          padding: "5px 12px",
                          cursor: removingPasskeyId === pk.credId ? "not-allowed" : "pointer",
                          opacity: removingPasskeyId === pk.credId ? 0.5 : 1,
                          transition: "opacity 0.15s",
                        }}
                      >
                        {removingPasskeyId === pk.credId ? "Removing..." : "Remove"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Single passkey: show remove inline */}
            {list.length === 1 && (
              <div style={{ marginBottom: "12px" }}>
                <button
                  onClick={() => { void handleRemovePasskey(list[0].credId); }}
                  disabled={removingPasskeyId === list[0].credId}
                  style={{
                    fontSize: "10px",
                    fontWeight: 500,
                    letterSpacing: "0.06em",
                    color: "#a85555",
                    background: "transparent",
                    border: "1px solid #3a1818",
                    borderRadius: "7px",
                    padding: "5px 12px",
                    cursor: removingPasskeyId === list[0].credId ? "not-allowed" : "pointer",
                    opacity: removingPasskeyId === list[0].credId ? 0.5 : 1,
                    transition: "opacity 0.15s",
                  }}
                >
                  {removingPasskeyId === list[0].credId ? "Removing..." : "Remove Passkey"}
                </button>
              </div>
            )}

            {/* Add another passkey */}
            <button
              onClick={() => { void handleLinkPasskey(); }}
              disabled={linkingPasskey}
              style={{
                ...ghostButtonStyle,
                opacity: linkingPasskey ? 0.6 : 1,
                cursor: linkingPasskey ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (!linkingPasskey) {
                  e.currentTarget.style.borderColor = "#272727";
                  e.currentTarget.style.color = "#d4cfc6";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#1e1e1e";
                e.currentTarget.style.color = "#888";
              }}
            >
              <span style={{ fontSize: "13px", lineHeight: 1 }}>&#43;</span>
              {linkingPasskey ? "Linking..." : "Add Another Passkey"}
            </button>
          </>
        ) : (
          <>
            {/* No passkey — magic link user CTA */}
            <div
              style={{
                padding: "16px",
                background: "#0a0a0a",
                border: "1px solid #1c1c1c",
                borderRadius: "10px",
                marginBottom: "14px",
              }}
            >
              <p style={{ fontSize: "11px", fontWeight: 500, color: "#666", margin: "0 0 4px" }}>
                No passkey linked
              </p>
              <p style={{ fontSize: "10px", color: "#333", lineHeight: "1.5", margin: 0 }}>
                Add a passkey to unlock on-chain features like deposits and withdrawals
              </p>
            </div>

            {passkeyError && (
              <p style={{ fontSize: "11px", color: "#a85555", marginBottom: "12px" }}>
                {passkeyError}
              </p>
            )}

            <Link
              href="/auth?tab=create"
              style={{
                ...ghostButtonStyle,
                display: "inline-flex",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#272727";
                e.currentTarget.style.color = "#d4cfc6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#1e1e1e";
                e.currentTarget.style.color = "#888";
              }}
            >
              <span style={{ fontSize: "13px", lineHeight: 1 }}>&#128274;</span>
              Add a Passkey
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
